import { useState, useEffect, useRef } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { useSlugNavigate } from '../hooks/useNavigation'
import DashboardLayout from '../components/DashboardLayout'
import { APP_VERSION, BUILD_DATE, BUILD_COMMIT_SHORT } from '../main'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:6200'
const ENVIRONMENT = import.meta.env.VITE_ENVIRONMENT || 'development'

export default function SettingsPage() {
  const { user, updateUser } = useAuth()
  const navigate = useSlugNavigate()
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)
  const [formData, setFormData] = useState({
    first_name: user?.first_name || '',
    last_name: user?.last_name || ''
  })
  const saveTimeoutRef = useRef(null)
  const successTimeoutRef = useRef(null)

  // Auto-save effect with debouncing
  useEffect(() => {
    // Clear existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current)
    }

    // Don't auto-save if no changes or invalid data
    if (!formData.first_name.trim() || !formData.last_name.trim()) {
      return
    }

    // Check if data has actually changed
    const hasChanges =
      formData.first_name !== user?.first_name ||
      formData.last_name !== user?.last_name

    if (!hasChanges) {
      return
    }

    // Set timeout to save after 1 second of no changes
    saveTimeoutRef.current = setTimeout(() => {
      handleAutoSave()
    }, 1000)

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData])

  // Cleanup success timeout on unmount
  useEffect(() => {
    return () => {
      if (successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current)
      }
    }
  }, [])

  const handleAutoSave = async () => {
    setSaving(true)
    setError('')
    setSuccess(false)

    try {
      const storedToken = localStorage.getItem('auth_token')
      const response = await fetch(`${API_URL}/api/v1/me/profile`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${storedToken}`
        },
        body: JSON.stringify(formData),
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to update profile')
      }

      // Update user in context
      updateUser(data)
      setSuccess(true)

      // Clear success message after 2 seconds
      if (successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current)
      }
      successTimeoutRef.current = setTimeout(() => setSuccess(false), 2000)
    } catch (err) {
      setError(err.message || 'Failed to update profile. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
    setError('') // Clear error on change
  }

  return (
    <DashboardLayout variant="settings">
      {/* Top Navigation Bar */}
      <header className="flex items-center justify-between whitespace-nowrap border-b border-solid border-[#dbe0e6] bg-white px-8 py-4 sticky top-0 z-10">
        <div className="flex items-center gap-4 text-[#111418]">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-[#617589] hover:text-primary transition-colors"
          >
            <span className="material-symbols-outlined text-[20px]">arrow_back</span>
            <span className="text-sm font-medium">Back to Dashboard</span>
          </button>
        </div>
      </header>

      {/* Page Content */}
      <div className="p-8 max-w-4xl mx-auto w-full">
        {/* Page Heading */}
        <div className="mb-8">
          <h1 className="text-[#111418] text-3xl font-black leading-tight tracking-[-0.033em] mb-2">
            Profile Settings
          </h1>
          <p className="text-[#617589] text-base font-normal leading-normal">
            Manage your personal information
          </p>
        </div>

        {/* Settings Card */}
        <div className="bg-white rounded-xl border border-[#dbe0e6] shadow-sm">
          {/* Profile Information Section */}
          <div className="px-6 py-5 border-b border-[#dbe0e6] flex items-center justify-between">
            <div>
              <h2 className="text-[#111418] text-lg font-bold leading-tight">Profile Information</h2>
              <p className="text-[#617589] text-sm mt-1">Changes are saved automatically</p>
            </div>
            {/* Saving Indicator */}
            <div className="flex items-center gap-2">
              {saving && (
                <div className="flex items-center gap-2 text-[#617589]">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                  <span className="text-sm">Saving...</span>
                </div>
              )}
              {success && (
                <div className="flex items-center gap-2 text-[#078838]">
                  <span className="material-symbols-outlined text-[18px]">check_circle</span>
                  <span className="text-sm font-medium">Saved</span>
                </div>
              )}
            </div>
          </div>

          <div className="p-6 space-y-6">
            {/* Email (Read-only) */}
            <label className="flex flex-col">
              <p className="text-[#111418] text-sm font-semibold leading-normal pb-2">
                Email Address
              </p>
              <input
                className="form-input flex w-full resize-none overflow-hidden rounded-lg text-[#617589] border border-[#dbe0e6] bg-[#f0f2f4] h-12 px-4 text-base font-normal leading-normal cursor-not-allowed"
                value={user?.email || ''}
                disabled
                type="email"
              />
              <p className="text-[#617589] text-xs mt-1">Email cannot be changed</p>
            </label>

            {/* First Name and Last Name Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <label className="flex flex-col">
                <p className="text-[#111418] text-sm font-semibold leading-normal pb-2">
                  First Name <span className="text-red-500">*</span>
                </p>
                <input
                  className="form-input flex w-full resize-none overflow-hidden rounded-lg text-[#111418] border border-[#dbe0e6] bg-white h-12 px-4 placeholder:text-[#617589] text-base font-normal leading-normal focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-shadow"
                  placeholder="John"
                  required
                  type="text"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleChange}
                />
              </label>

              <label className="flex flex-col">
                <p className="text-[#111418] text-sm font-semibold leading-normal pb-2">
                  Last Name <span className="text-red-500">*</span>
                </p>
                <input
                  className="form-input flex w-full resize-none overflow-hidden rounded-lg text-[#111418] border border-[#dbe0e6] bg-white h-12 px-4 placeholder:text-[#617589] text-base font-normal leading-normal focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-shadow"
                  placeholder="Doe"
                  required
                  type="text"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleChange}
                />
              </label>
            </div>

            {/* Error Message */}
            {error && (
              <div className="rounded-lg bg-red-50 p-4 border border-red-200">
                <div className="flex items-start">
                  <span className="material-symbols-outlined text-red-600 mr-3 text-[20px]">error</span>
                  <p className="text-sm font-medium text-red-800">{error}</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Version Info */}
        <div className="mt-8 text-center text-[#617589] text-xs">
          <p>
            Version {APP_VERSION} • {ENVIRONMENT}
            {BUILD_DATE !== 'dev' && (
              <span className="ml-2">• Built {new Date(BUILD_DATE).toLocaleDateString()}</span>
            )}
          </p>
        </div>
      </div>
    </DashboardLayout>
  )
}
